// @flow

import React, { Component } from "react";
import PrivateNavbar from "./PrivateNavbar";
import PrivateSidebar from "./PrivateSidebar";
import { withStyles } from "@material-ui/core/styles";
import classNames from "classnames";
import { List, Map } from "immutable";

const drawerWidth = 250;
const desktopToolbarHeight = 65;

const styles = (theme) => ({
  appFrame: {
    position: "relative",
    display: "flex",
    width: "100%",
    height: "100%",
  },
  animatorVertical: {
    zIndex: 4,
    transition: "350ms ease-in-out",
    position: "fixed",
    height: "100%",
    transform: "translateY(0px)",
  },
  animatorVerticalStickied: {
    transform: `translateY(-${desktopToolbarHeight}px)`,
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    [theme.breakpoints.up("lg")]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
    },
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaper: {
    position: "fixed",
    height: "calc(100% - " + desktopToolbarHeight + ")",
    width: drawerWidth,
    marginTop: desktopToolbarHeight,
    zIndex: 3,
    overflowX: "hidden",
  },
  drawerPaperStickied: {
    marginTop: 0,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    ...theme.mixins.toolbar,
  },
  content: {
    width: "100%",
    marginLeft: 0,
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginTop: desktopToolbarHeight * 2,
  },
  contentShift: {
    [theme.breakpoints.up("lg")]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth / 2}px)`,
    },
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
});

type Props = {
  closeDrawer: Function,
  isDrawerOpen: boolean,
  isMobileDrawerOpen: boolean,
  items: List<Map<string, any>>,
  currentUser: Map<string, any>,
  canSeeContest: boolean,
  isAdmin: boolean,
  pathname: string,
  redirect: (string) => void,
  onButtonClick: Function,
  children: any,
  classes: Object,
  logout: Function,
  dispatch: (Object) => void,
};

type State = {
  stickied: boolean,
};

class PrivateAppFrame extends Component<Props, State> {
  state: State;
  // private properties of this component
  stickyListener: ?EventListener;
  lastScrollTop: number;
  scrollListener: (Event) => void;

  constructor(props: Props) {
    super(props);
    this.state = { stickied: false };
    this.lastScrollTop = 0;
    this.stickyListener = null;
    this.scrollListener = this._scrollListener.bind(this);
  }

  _scrollListener(event: Event) {
    if (document.body) {
      // to target all browsers
      const scroll =
        window.pageYOffset ||
        (document.documentElement && document.documentElement.scrollTop) ||
        document.body.scrollTop ||
        0;
      if (scroll > this.lastScrollTop) {
        this.lastScrollTop = scroll;
        this.setState({ stickied: true });
      } else if (scroll === 0 && this.lastScrollTop > 0) {
        this.lastScrollTop = scroll;
        this.setState({ stickied: false });
      }
    }
  }

  UNSAFE_componentWillMount() {
    if (typeof window !== "undefined") {
      this.stickyListener = window.addEventListener(
        "scroll",
        this.scrollListener
      );
    }
  }

  componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener(this.stickyListener, this.scrollListener);
    }
  }

  render() {
    const {
      closeDrawer,
      isDrawerOpen,
      isMobileDrawerOpen,
      items,
      currentUser,
      canSeeContest,
      isAdmin,
      pathname,
      redirect,
      onButtonClick,
      children,
      classes,
      logout,
      dispatch,
    } = this.props;
    const { stickied } = this.state;
    const animatorVerticalStyles = stickied
      ? `${classes.animatorVertical} ${classes.animatorVerticalStickied}`
      : classes.animatorVertical;
    return (
      <div className={classes.appFrame}>
        <div className={animatorVerticalStyles}>
          <PrivateSidebar
            close={closeDrawer}
            isOpen={isDrawerOpen}
            isOpenMobile={isMobileDrawerOpen}
            items={items}
            pathname={pathname}
            classes={{
              paper: classes.drawerPaper,
            }}
            dispatch={dispatch}
          />
        </div>
        <PrivateNavbar
          className={classNames(
            classes.appBar,
            isDrawerOpen && classes.appBarShift
          )}
          currentUser={currentUser}
          canSeeContest={canSeeContest}
          isAdmin={isAdmin}
          pathname={pathname}
          redirect={redirect}
          onButtonClick={onButtonClick}
          stickied={stickied}
          logout={logout}
        />
        <main
          className={classNames(
            classes.content,
            isDrawerOpen && classes.contentShift
          )}
        >
          {children}
        </main>
      </div>
    );
  }
}

export default withStyles(styles)(PrivateAppFrame);
